import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Events,
  TreatmentPlan,
  TreatmentPlanItem,
  TreatmentPlansRepositoryService,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientTreatmentPlansStore — saved treatment-plan snapshots for the
 * current patient. Plans are immutable post-save: there is no draft
 * editor and no update flow. PDF print lives on the cross-cutting
 * documents seam (planned DocumentsService follow-up) and is not
 * modelled here.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientTreatmentPlansStore {
  private patientStore = inject(PatientStore);
  private repo = inject(TreatmentPlansRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<TreatmentPlan[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.TreatmentPlanTreatmentPlansChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Snapshot the supplied items into a new saved treatment plan.
   * Mirrors PatientScriptsStore.createFromMedication. The items pool
   * (PatientTreatmentPlanItemsStore) is not mutated by this call.
   */
  createFromItems(items: TreatmentPlanItem[]): Observable<number> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientTreatmentPlansStore.createFromItems() called with no current patient');
    }
    return this.repo.savePlan(patient.id, items).pipe(
      tap({
        next: () => this.reload()
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientTreatmentPlans(patientId).subscribe({
      next: plans => {
        const sorted = [...plans].sort((a, b) =>
          a.createdDate > b.createdDate ? 1 : -1
        );
        this._items.set(sorted);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient treatment plans');
        
      }
    });
  }
}
